import React, { FC, useCallback } from 'react';
import { Permission, UserPermissionVerbose } from '../../../models';
import { Fetcher, useFetcher } from '../../../utils';
import { captureError } from '../../../utils/errorHandling';
import { PermissionEnum, PermissionListComponent } from '../roles';

interface TabDetailsProps {
  readonly userId: number;
  readonly canEdit: boolean;
}

interface LoadingData {
  userPermissions: UserPermissionVerbose[];
  permissions: Permission[];
}

export const UserPermissionsTab: FC<TabDetailsProps> = ({ userId, canEdit }) => {
  const update = useCallback(
    async (permissionId: number, state: PermissionEnum) => {
      try {
        await (state === PermissionEnum.Inherit
          ? api.deleteUserPermission(userId, permissionId)
          : api.updateUserPermission(userId, permissionId, state === PermissionEnum.Deny));
      } catch (error) {
        captureError(error as Error);
      }
    },
    [userId],
  );

  const fetcher = useFetcher(async (): Promise<LoadingData> => {
    const [permissions, userPermissions] = await Promise.all([
      api.getPermissions(),
      api.getUserPermissions(userId),
    ]);

    return {
      permissions,
      userPermissions,
    };
  }, [userId]);

  if (!fetcher.data) {
    return <Fetcher result={fetcher} />;
  }

  const { userPermissions, permissions } = fetcher.data;

  return (
    <PermissionListComponent
      canEdit={canEdit}
      items={userPermissions}
      permissions={permissions}
      update={async (a, b) => {
        await update(a, b).then(() => {
          fetcher.refresh();
        });
      }}
    />
  );
};
